// SignalR / WebSocket rider client. Mirrors flutter_client SignalRService
// (lib/services/signal_r_service.dart): pure-WebSockets transport with
// skipNegotiation, JWT carried via the `access_token` query param through
// accessTokenFactory, an infinite reconnect policy, and the three server
// events the rider hub emits: `new_offer_on_order`, `offer_withdrawn`, and
// `set_driver_location` (realtime driver coordinates).
//
// The server wraps messages as `{ data, messageId }` and retries until the
// client ACKs with `message_received` — so every envelope with a messageId
// must be confirmed or the server will keep re-sending. The rider also has to
// call `send_location` periodically (every 15s) to bind to the assigned
// driver's personal channel and extend the subscription TTL.

import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import type { RetryContext } from '@microsoft/signalr';
import { config } from './config';
import { tokenStore } from './tokenStore';

export type DriverLocationEvent = {
  driverId?: string;
  orderId?: string;
  lat: number;
  lon: number;
};

export type RealtimeHandlers = {
  onDriverLocation?: (e: DriverLocationEvent) => void;
  onNewOffer?: (data: Record<string, unknown>) => void;
  onOfferWithdrawn?: (data: Record<string, unknown>) => void;
  onStateChange?: (connected: boolean) => void;
};

// Default DefaultReconnectPolicy gives up after ~42s ([0,2,10,30,null]). For an
// active trip a tunnel/lift/dead-zone longer than that would kill the socket
// for the whole ride, so we retry indefinitely: 0 -> 2s -> 5s -> every 10s,
// with ±20% jitter so mass server restarts don't reconnect in lockstep.
const RECONNECT_SCHEDULE = [0, 2000, 5000, 10000];
function infiniteRetryDelay(rc: RetryContext): number | null {
  const attempt = rc.previousRetryCount ?? 0;
  const base =
    attempt < RECONNECT_SCHEDULE.length
      ? RECONNECT_SCHEDULE[attempt]
      : RECONNECT_SCHEDULE[RECONNECT_SCHEDULE.length - 1];
  if (base === 0) return 0;
  const jitter = Math.round(base * 0.2 * (Math.random() * 2 - 1));
  return Math.max(0, Math.min(15000, base + jitter));
}

const LOCATION_PUSH_INTERVAL_MS = 15_000;
const KEEPALIVE_MS = 15_000;
const SERVER_TIMEOUT_MS = 60_000;
const RECONNECT_BACKOFF_MS = 3_000;

export class RealtimeService {
  private conn: HubConnection | null = null;
  private handlers: RealtimeHandlers = {};
  private stopped = true;
  private pushTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lat = 0;
  private lon = 0;

  get isConnected(): boolean {
    return this.conn?.state === HubConnectionState.Connected;
  }

  setHandlers(h: RealtimeHandlers): void {
    this.handlers = h;
  }

  // Latest rider position, sent in `initializeAsync` and the periodic
  // `send_location` push. The value matters less than the periodic invoke —
  // that is what keeps the rider bound to the driver's channel.
  updateLocation(lat: number, lon: number): void {
    this.lat = lat;
    this.lon = lon;
  }

  async start(): Promise<void> {
    if (!config.socketUrl) {
      console.warn('[realtime] VITE_SOCKET_URL is empty — realtime disabled');
      return;
    }
    if (this.conn) return;
    this.stopped = false;

    const conn = new HubConnectionBuilder()
      .withUrl(config.socketUrl, {
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
        accessTokenFactory: () => tokenStore.getAccess() ?? '',
      })
      .withAutomaticReconnect({ nextRetryDelayInMilliseconds: infiniteRetryDelay })
      .configureLogging(LogLevel.Warning)
      .build();

    conn.serverTimeoutInMilliseconds = SERVER_TIMEOUT_MS;
    conn.keepAliveIntervalInMilliseconds = KEEPALIVE_MS;

    conn.onreconnected(async () => {
      this.handlers.onStateChange?.(true);
      await this.initialize();
      await this.sendLocation();
    });
    conn.onclose(() => {
      this.handlers.onStateChange?.(false);
      // Auto-recover with a fresh token (e.g. JWT expired mid-trip). The
      // built-in policy handles transient drops; this covers the "policy gave
      // up / closed with error" case, mirroring flutter's onclose logic.
      if (!this.stopped) {
        this.scheduleReconnect();
      }
    });

    conn.on('set_driver_location', (...args) => this.handleDriverLocation(args));
    conn.on('new_offer_on_order', (...args) =>
      this.unwrap(args, (d) => this.handlers.onNewOffer?.(d)),
    );
    conn.on('offer_withdrawn', (...args) =>
      this.unwrap(args, (d) => this.handlers.onOfferWithdrawn?.(d)),
    );

    this.conn = conn;
    try {
      await conn.start();
      this.handlers.onStateChange?.(true);
      await this.initialize();
      await this.sendLocation();
      this.startPushTimer();
    } catch (e) {
      console.error('[realtime] connect failed', e);
      // The builder left a half-built connection; clear it so start() can retry.
      this.conn = null;
      if (!this.stopped) this.scheduleReconnect();
    }
  }

  async stop(): Promise<void> {
    this.stopped = true;
    this.stopPushTimer();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const c = this.conn;
    this.conn = null;
    if (c) {
      try {
        await c.stop();
      } catch {
        // ignore
      }
    }
    this.handlers.onStateChange?.(false);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.conn) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.start();
    }, RECONNECT_BACKOFF_MS);
  }

  // `initializeAsync` re-subscribes the rider to the geofones/driver channel
  // after (re)connect. Args shape matches the Dart client.
  private async initialize(): Promise<void> {
    const c = this.conn;
    if (!c) return;
    try {
      await c.invoke('initializeAsync', JSON.stringify({ lat: this.lat, lon: this.lon }), '');
    } catch {
      // non-fatal: position pushes will still bind the channel
    }
  }

  async sendLocation(): Promise<void> {
    const c = this.conn;
    if (!c || c.state !== HubConnectionState.Connected) return;
    if (this.lat === 0 && this.lon === 0) return;
    try {
      await c.invoke('SendMessage', 'send_location', JSON.stringify({ lat: this.lat, lon: this.lon }));
    } catch {
      // ignore — next push tick retries
    }
  }

  private startPushTimer(): void {
    this.stopPushTimer();
    this.pushTimer = setInterval(() => {
      if (this.isConnected) void this.sendLocation();
    }, LOCATION_PUSH_INTERVAL_MS);
  }

  private stopPushTimer(): void {
    if (this.pushTimer) {
      clearInterval(this.pushTimer);
      this.pushTimer = null;
    }
  }

  // Server envelope: `{ data, messageId }`. Confirm receipt so the server stops
  // retrying, then hand the payload to the handler.
  private unwrap(
    args: unknown,
    cb: (data: Record<string, unknown>) => void,
  ): void {
    if (!Array.isArray(args) || args.length === 0) return;
    const raw = args[0];
    if (!raw || typeof raw !== 'object') return;
    const map = raw as Record<string, unknown>;

    if (map['messageId'] != null && typeof map['data'] === 'object' && map['data']) {
      const messageId = String(map['messageId']);
      if (messageId) void this.confirm(messageId);
      cb(map['data'] as Record<string, unknown>);
      return;
    }
    cb(map);
  }

  private async confirm(messageId: string): Promise<void> {
    const c = this.conn;
    if (!c || c.state !== HubConnectionState.Connected) return;
    try {
      await c.invoke('SendMessage', 'message_received', JSON.stringify({ messageId }));
    } catch {
      // ignore
    }
  }

  private handleDriverLocation(args: unknown): void {
    this.unwrap(args, (data) => {
      const lat = data['lat'] ?? data['latitude'];
      const lon = data['lon'] ?? data['longitude'];
      if (typeof lat !== 'number' || typeof lon !== 'number') return;
      this.handlers.onDriverLocation?.({
        driverId: (data['id'] ?? data['driverId'])?.toString(),
        orderId: data['orderId']?.toString(),
        lat,
        lon,
      });
    });
  }
}
